import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { copyToClipboard, getCopyIcon } from '../../utils/copyUtils';

const VoiceAITranscriptViewer = ({
  transcript, transcriptObject = null,
  isAnkrAdmin = false,
  maxHeight = '80vh', className = ''
}) => {
  const [copyIconState, setCopyIconState] = useState({});
  const [rawExpanded, setRawExpanded] = useState(false);

  if (!transcript) {
    return (
      <div className={`text-center py-5 ${className}`}>
        <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
        <p className="text-muted">No transcript available. Try again in a minute.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Collapsible Raw Transcript - Admin Only */}
      {isAnkrAdmin && (
        <div className="card mb-3">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <button
                className="btn btn-link text-primary p-0 text-decoration-none d-flex align-items-center"
                onClick={() => setRawExpanded(!rawExpanded)}
                type="button"
              >
                <i className={`fas fa-chevron-${rawExpanded ? 'down' : 'right'} me-2`}></i>
                <h6 className="card-title text-primary mb-0 pb-0 border-0">Raw Transcript</h6>
              </button>
              <div className="d-flex d-flex-inline gap-2">
                <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setRawExpanded(!rawExpanded)}
                type="button"
                ><i className={`fas ${rawExpanded ? 'fa-eye-slash':'fa-eye'} me-1`}></i>
                View</button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => copyToClipboard(transcript, setCopyIconState, 'raw-transcript')}
                >
                  <i className={`fas ${getCopyIcon(copyIconState['raw-transcript'])} me-1`}></i>
                  {copyIconState['raw-transcript'] === 'success' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            {rawExpanded && (
              <pre className="bg-light p-3 rounded" style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                {transcript}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* Bubble View - Always Visible */}
      <div className="card">
        <div className="card-body">
          <h6 className="card-title text-primary mb-3">
            Conversation
          </h6>
          <div className="transcript-timeline" style={{ maxHeight, overflowY: 'auto' }}>
            {(() => {
              // Group multi-line messages together
              const lines = transcript.split('\n').filter(line => line.trim());
              const messages = [];
              let currentMessage = null;

              lines.forEach(line => {
                const isAgent = line.startsWith('Agent: ');
                const isUser = line.startsWith('User: ');

                if (isAgent || isUser) {
                  // Start a new message
                  if (currentMessage) {
                    messages.push(currentMessage);
                  }
                  currentMessage = {
                    type: isAgent ? 'agent' : 'user',
                    content: line.substring(isAgent ? 7 : 6)
                  };
                } else if (currentMessage) {
                  // Continuation of the current message
                  currentMessage.content += '\n' + line;
                }
              });

              // Push the last message
              if (currentMessage) {
                messages.push(currentMessage);
              }

              if (messages.length === 0) {
                return (
                  <div className="text-center py-3 text-muted">
                    <i className="fas fa-comment-slash me-2"></i>
                    No messages to display
                  </div>
                );
              }

              return messages.map((message, idx) => {
                const isAgent = message.type === 'agent';
                const isLastMessage = idx === messages.length - 1;

                return (
                  <div key={idx} className={`d-flex mb-2 ${isAgent ? 'justify-content-end' : 'justify-content-start'}`} style={{ opacity: isLastMessage ? 1 : 0.8 }}>
                    <div className={`p-2 rounded ${isAgent ? 'bg-primary text-white' : 'bg-secondary bg-opacity-25'}`} style={{ maxWidth: '70%', whiteSpace: 'pre-wrap' }}>
                      <div className="mb-1">
                        <i className={`mb-2 fas ${isAgent ? 'fa-robot' : 'fa-user'} me-2`}></i>
                        {isAgent ? 'Agent' : 'User'}
                      </div>
                      {message.content}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

VoiceAITranscriptViewer.propTypes = {
  transcript: PropTypes.string,
  transcriptObject: PropTypes.array,
  isAnkrAdmin: PropTypes.bool,
  maxHeight: PropTypes.string,
  className: PropTypes.string
};

export default VoiceAITranscriptViewer;